import { useId, useState, useMemo, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { accessLogService, AccessLog, AccessLogListParams } from '../services/accessLog.service';
import { KENSUKE_FEATURE_CARD_DEFS } from '../constants/kensukeFeatureCards';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import AccessLogStatistics from '../components/access-logs/AccessLogStatistics';
import { formatUserName } from '../utils/name.util';
import { getDateFnsLocale } from '../utils/dateFnsLocale';
import type { TFunction } from 'i18next';
import { summarizeUserAgent } from '../utils/parseUserAgent';
import { labelForAppSurface, ACCESS_LOG_APP_SURFACE_LABEL_KEYS } from '../utils/accessLogAppLabels';
import { formatDurationMs } from '../utils/formatDuration';
import styles from './AccessLogs.module.scss';

const LIMIT_OPTIONS = [20, 50, 100] as const;

const ACCESS_LOG_ACTION_LABEL_KEYS: Record<string, string> = {
  view: 'access_logs.action.view',
  create: 'access_logs.action.create',
  update: 'access_logs.action.update',
  delete: 'access_logs.action.delete',
  add_usage_record: 'access_logs.action.add_usage_record',
  page_view: 'access_logs.action.page_view',
  page_engagement: 'access_logs.action.page_engagement',
  feature_entrance: 'access_logs.action.feature_entrance',
  auth_block: 'access_logs.action.auth_block',
};

const PUBLIC_PAGE_PATH_LABEL_KEYS: Record<string, string> = {
  '/': 'access_logs.path.top',
  '/register': 'access_logs.path.register',
  '/terms': 'access_logs.path.terms',
  '/privacy': 'access_logs.path.privacy',
  '/help': 'access_logs.path.help',
  '/contact': 'access_logs.path.contact',
  '/announcements': 'access_logs.path.announcements',
  '/login': 'access_logs.path.login',
  '/keisuke': 'access_logs.path.keisuke',
  '/gaisuke': 'access_logs.path.gaisuke',
  '/arena/keiba': 'access_logs.path.keiba_arena',
  '/keiba-arena': 'access_logs.path.keiba_arena',
  '/arena/banquet': 'access_logs.path.banquet',
  '/arena/econ': 'access_logs.path.econ_report',
  '/arena/fx': 'access_logs.path.gaisuke_fx',
  '/labo-suke': 'access_logs.path.labo_suke',
  '/transit-pass-pool': 'access_logs.path.transit_pass',
  '/gene-analysis': 'access_logs.path.gene_analysis',
  '/samples': 'access_logs.path.samples',
};

const HTTP_METHOD_VARIANT: Record<string, string> = {
  GET: 'success',
  POST: 'primary',
  PUT: 'warning',
  PATCH: 'info',
  DELETE: 'danger',
  HEAD: 'secondary',
  OPTIONS: 'secondary',
};

function httpMethodBadge(method: string | null): React.ReactNode {
  if (!method) return null;
  const bg = HTTP_METHOD_VARIANT[method.toUpperCase()] ?? 'secondary';
  return <Badge bg={bg} className="me-1">{method}</Badge>;
}

function statusCodeBadge(code: number | null): React.ReactNode {
  if (code == null) return null;
  const bg = code < 300 ? 'success' : code < 400 ? 'info' : code < 500 ? 'warning' : 'danger';
  return <Badge bg={bg}>{code}</Badge>;
}

function displayUser(log: AccessLog, t: TFunction, language: string): string {
  if (log.user) {
    return formatUserName(log.user.firstName, log.user.lastName, language) || log.user.username || '-';
  }
  if (log.usernameSnapshot) return `${log.usernameSnapshot} †`;
  return t('access_logs.table.anonymous', { defaultValue: '匿名' });
}

function displaySample(log: AccessLog): string {
  if (log.sample) return log.sample.name;
  if (log.sampleNameSnapshot) return `${log.sampleNameSnapshot} †`;
  return '-';
}

function formatActionDisplay(log: AccessLog, t: TFunction): string {
  if (log.action === 'api') {
    return t('access_logs.action.api_audit', { defaultValue: 'API（全体監査）' });
  }
  if (typeof log.action === 'string' && log.action.startsWith('security_')) {
    const kind = log.action.replace(/^security_/, '');
    const labels: Record<string, string> = {
      login_failed: 'ログイン失敗',
      login_blocked: 'ログインの連続失敗',
      admin_denied: '管理者以外の管理操作',
      auth_required: '未ログインの管理操作',
    };
    return labels[kind] || log.action;
  }
  if (typeof log.action === 'string' && log.action.startsWith('auth_block_')) {
    const code = log.action.replace(/^auth_block_/, '');
    return t('access_logs.action.auth_block_row', {
      defaultValue: '認証ブロック（HTTP {{code}}）',
      code,
    });
  }
  const labelKey = ACCESS_LOG_ACTION_LABEL_KEYS[log.action];
  if (labelKey) {
    return t(labelKey, { defaultValue: log.action });
  }
  return log.action;
}

function formatPathDisplay(log: AccessLog, t: TFunction): string {
  const raw = log.path?.trim();
  if (!raw) return '-';
  if (log.action === 'feature_entrance') {
    const def = KENSUKE_FEATURE_CARD_DEFS.find((d) => d.id === raw);
    if (def) {
      return t(def.titleKey, { defaultValue: raw });
    }
    return raw;
  }
  const localeStripped = raw.replace(/^\/(ja|en|zh-CN|fr|es|de|pt|ru)(?=\/)/, '');
  const pathLabelKey =
    PUBLIC_PAGE_PATH_LABEL_KEYS[raw] ?? PUBLIC_PAGE_PATH_LABEL_KEYS[localeStripped];
  if (pathLabelKey) {
    return t(pathLabelKey, { defaultValue: raw });
  }
  if (raw.startsWith('/reservations/share') || localeStripped.startsWith('/reservations/share')) {
    return t('access_logs.path.reservation_demo', { defaultValue: '予約デモ' });
  }
  if (raw.startsWith('/keisuke/') || localeStripped.startsWith('/keisuke/')) {
    return t('access_logs.path.keisuke_sub', { defaultValue: '計助: {{path}}', path: raw });
  }
  return raw;
}

const EXPORT_LIMIT = 10000;

export default function AccessLogs() {
  const { t, i18n } = useTranslation();
  const embedded = useLocation().pathname.startsWith('/admin/insights/');
  const dateFnsLocale = getDateFnsLocale(i18n.language);
  const fieldId = useId();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<typeof LIMIT_OPTIONS[number]>(LIMIT_OPTIONS[0]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [path, setPath] = useState<string>('');
  const [isPublicAccess, setIsPublicAccess] = useState<string>('');
  const [sampleId, setSampleId] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [httpMethod, setHttpMethod] = useState<string>('');
  const [statusCode, setStatusCode] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showStatistics, setShowStatistics] = useState(true);
  const [appSurfaceFilter, setAppSurfaceFilter] = useState<string>('');

  const listParams = useMemo<AccessLogListParams>(() => {
    const params: AccessLogListParams = {
      page: Math.max(1, page),
      limit: LIMIT_OPTIONS.includes(limit) ? limit : LIMIT_OPTIONS[0],
    };
    if (startDate) params.startDate = `${startDate}T00:00:00`;
    if (endDate) params.endDate = `${endDate}T23:59:59`;
    if (action) params.action = action;
    if (path) params.path = path;
    if (isPublicAccess === 'true') params.isPublicAccess = true;
    if (isPublicAccess === 'false') params.isPublicAccess = false;
    const parsedSampleId = parseInt(sampleId, 10);
    const parsedUserId = parseInt(userId, 10);
    if (sampleId.trim() !== '' && !Number.isNaN(parsedSampleId) && parsedSampleId >= 1)
      params.sampleId = parsedSampleId;
    if (userId.trim() !== '' && !Number.isNaN(parsedUserId) && parsedUserId >= 1)
      params.userId = parsedUserId;
    if (httpMethod) params.httpMethod = httpMethod;
    const parsedStatus = parseInt(statusCode, 10);
    if (statusCode.trim() !== '' && !Number.isNaN(parsedStatus) && parsedStatus >= 100)
      params.statusCode = parsedStatus;
    if (appSurfaceFilter) params.appSurface = appSurfaceFilter;
    return params;
  }, [page, limit, startDate, endDate, action, path, isPublicAccess, sampleId, userId, httpMethod, statusCode, appSurfaceFilter]);

  const filterParamsForExport = useMemo(() => {
    const parsedSampleId = parseInt(sampleId, 10);
    const parsedUserId = parseInt(userId, 10);
    const parsedStatus = parseInt(statusCode, 10);
    return {
      startDate: startDate ? `${startDate}T00:00:00` : undefined,
      endDate: endDate ? `${endDate}T23:59:59` : undefined,
      action: action || undefined,
      path: path || undefined,
      isPublicAccess: isPublicAccess === 'true' ? true : isPublicAccess === 'false' ? false : undefined,
      sampleId:
        sampleId.trim() !== '' && !Number.isNaN(parsedSampleId) && parsedSampleId >= 1
          ? parsedSampleId
          : undefined,
      userId:
        userId.trim() !== '' && !Number.isNaN(parsedUserId) && parsedUserId >= 1
          ? parsedUserId
          : undefined,
      httpMethod: httpMethod || undefined,
      statusCode:
        statusCode.trim() !== '' && !Number.isNaN(parsedStatus) && parsedStatus >= 100
          ? parsedStatus
          : undefined,
      appSurface: appSurfaceFilter || undefined,
    };
  }, [startDate, endDate, action, path, isPublicAccess, sampleId, userId, httpMethod, statusCode, appSurfaceFilter]);

  useEffect(() => {
    setExportError(null);
  }, [startDate, endDate, action, path, isPublicAccess, sampleId, userId, httpMethod, statusCode, appSurfaceFilter]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['access-logs', listParams],
    queryFn: () => accessLogService.list(listParams),
  });

  const { data: statisticsData, isLoading: isStatisticsLoading } = useQuery({
    queryKey: ['access-logs-statistics', listParams.startDate, listParams.endDate],
    queryFn: () =>
      accessLogService.getStatistics({
        startDate: listParams.startDate,
        endDate: listParams.endDate,
      }),
    staleTime: 0,
    enabled: showStatistics,
  });

  const logs = data?.data ?? [];
  const pagination = data?.pagination;

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const res = await accessLogService.list({
        ...filterParamsForExport,
        page: 1,
        limit: EXPORT_LIMIT,
      });
      const rows = res.data;
      const header = [
        t('access_logs.table.header_access_time', { defaultValue: 'アクセス日時' }),
        t('access_logs.table.header_user', { defaultValue: 'ユーザー' }),
        t('access_logs.table.header_user_snapshot', { defaultValue: 'ユーザー名スナップショット' }),
        t('access_logs.table.header_user_email_snapshot', { defaultValue: 'メールスナップショット' }),
        t('access_logs.table.header_action', { defaultValue: 'アクション' }),
        t('access_logs.statistics.app_column', { defaultValue: 'アプリ' }),
        t('access_logs.table.header_http_method', { defaultValue: 'HTTPメソッド' }),
        t('access_logs.table.header_status_code', { defaultValue: 'ステータスコード' }),
        t('access_logs.table.header_duration_ms', { defaultValue: '処理時間(ms)' }),
        t('access_logs.table.header_sample', { defaultValue: 'サンプル' }),
        t('access_logs.table.header_sample_snapshot', { defaultValue: 'サンプル名スナップショット' }),
        t('access_logs.table.header_page', { defaultValue: 'パス' }),
        t('access_logs.table.header_ip_address', { defaultValue: 'IPアドレス' }),
        t('access_logs.table.header_country', { defaultValue: '国' }),
        t('access_logs.table.header_ip_chain', { defaultValue: 'IPチェーン' }),
        t('access_logs.table.header_request_id', { defaultValue: 'リクエストID' }),
        t('access_logs.table.header_referer', { defaultValue: 'リファラー' }),
        t('access_logs.table.header_user_agent', { defaultValue: '環境' }),
        t('access_logs.table.header_access_type', { defaultValue: 'アクセスタイプ' }),
      ];
      const escape = (v: string) => {
        let s = String(v ?? '');
        if (/^[=+\-@\t\r]/.test(s)) s = "'" + s.replace(/'/g, "''");
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const toUserLabel = (log: AccessLog) => {
        if (!log.user) return t('access_logs.table.anonymous', { defaultValue: '匿名' });
        return formatUserName(log.user.firstName, log.user.lastName, i18n.language) || log.user.username || '-';
      };
      const toAccessType = (log: AccessLog) =>
        log.isPublicAccess
          ? t('access_logs.table.public', { defaultValue: '公開' })
          : t('access_logs.table.private', { defaultValue: '非公開' });
      const toDateStr = (d: string) => {
        try {
          return format(new Date(d), 'yyyy/MM/dd HH:mm:ss', { locale: dateFnsLocale });
        } catch {
          return d;
        }
      };
      const csvRows = [
        header.map(escape).join(','),
        ...rows.map((log) =>
          [
            toDateStr(log.accessTime),
            toUserLabel(log),
            log.usernameSnapshot ?? '',
            log.userEmailSnapshot ?? '',
            formatActionDisplay(log, t),
            log.httpMethod ?? '',
            log.statusCode != null ? String(log.statusCode) : '',
            log.durationMs != null ? String(log.durationMs) : '',
            log.sample?.name ?? '',
            log.sampleNameSnapshot ?? '',
            formatPathDisplay(log, t),
            labelForAppSurface(log.appSurface, t),
            log.ipAddress,
            log.countryCode ?? '',
            log.ipChain ?? '',
            log.requestId ?? '',
            log.referer ?? '',
            log.clientEnvironment ?? summarizeUserAgent(log.userAgent),
            toAccessType(log),
          ].map((cell) => escape(String(cell))).join(',')
        ),
      ];
      const bom = '﻿';
      const blob = new Blob([bom + csvRows.join('\r\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `access-logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setExportError(t('access_logs.export_error', { defaultValue: 'エクスポートに失敗しました。' }));
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setAction('');
    setPath('');
    setIsPublicAccess('');
    setSampleId('');
    setUserId('');
    setHttpMethod('');
    setStatusCode('');
    setPage(1);
  };

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {t('access_logs.error_loading', { defaultValue: 'アクセスログの読み込みに失敗しました。' })}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className={`py-2 ${styles.page}`}>
      {!embedded && (
        <>
          <h1 className="h3 mb-2">{t('access_logs.title', { defaultValue: 'アクセスログ' })}</h1>
          <p className={`text-muted small ${styles.lead}`}>
            {t('access_logs.description', { defaultValue: 'サンプル管理システムへのアクセスログを閲覧・検索できます。' })}
          </p>
        </>
      )}

      <CollapsibleHelpSection
        titleKey="access_logs.help_title"
        storageKey="access_logs"
        descriptionKey="access_logs.help_content"
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button
          variant={showStatistics ? 'secondary' : 'outline-secondary'}
          size="sm"
          onClick={() => setShowStatistics((v) => !v)}
          aria-expanded={showStatistics}
        >
          {showStatistics
            ? t('access_logs.statistics.hide', { defaultValue: '統計情報を隠す' })
            : t('access_logs.statistics.show', { defaultValue: '統計情報を表示' })}
        </Button>
      </div>
      {showStatistics && isStatisticsLoading && (
        <div className="text-center py-2" role="status" aria-label={t('common.loading', { defaultValue: '読み込み中' })}>
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {showStatistics && !isStatisticsLoading && statisticsData?.data && (
        <AccessLogStatistics statistics={statisticsData.data} />
      )}

      {/* フィルター */}
      <Card className="mb-3">
        <Card.Header>{t('access_logs.filters.title', { defaultValue: 'フィルター' })}</Card.Header>
        <Card.Body>
          <Row className="g-2">
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-start-date`}>
                {t('access_logs.filters.date_range', { defaultValue: '開始日' })}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-filter-start-date`}
                name="accessLogFilterStartDate"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                lang={i18n.language}
              />
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-end-date`}>
                {t('access_logs.filters.date_range_end', { defaultValue: '終了日' })}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-filter-end-date`}
                name="accessLogFilterEndDate"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                lang={i18n.language}
              />
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-action`}>
                {t('access_logs.filters.action', { defaultValue: 'アクション' })}
              </Form.Label>
              <Form.Select
                id={`${fieldId}-filter-action`}
                name="accessLogFilterAction"
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                <option value="view">{t('access_logs.action.view', { defaultValue: '閲覧' })}</option>
                <option value="create">{t('access_logs.action.create', { defaultValue: '作成' })}</option>
                <option value="update">{t('access_logs.action.update', { defaultValue: '更新' })}</option>
                <option value="delete">{t('access_logs.action.delete', { defaultValue: '削除' })}</option>
                <option value="add_usage_record">{t('access_logs.action.add_usage_record', { defaultValue: '使用記録追加' })}</option>
                <option value="page_view">{t('access_logs.action.page_view', { defaultValue: 'ページ閲覧' })}</option>
                <option value="page_engagement">
                  {t('access_logs.action.page_engagement', { defaultValue: 'ページ滞在' })}
                </option>
                <option value="feature_entrance">
                  {t('access_logs.action.feature_entrance', { defaultValue: 'ツール入口クリック' })}
                </option>
                <option value="security">
                  {t('access_logs.action.security', { defaultValue: '試行（ログイン失敗・管理操作の拒否）' })}
                </option>
                <option value="auth_block">
                  {t('access_logs.action.auth_block', {
                    defaultValue: '認証ブロック（レート制限・サーバ不可等）',
                  })}
                </option>
                <option value="api">{t('access_logs.action.api_audit', { defaultValue: 'API（全体監査）' })}</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-http-method`}>
                {t('access_logs.filters.http_method', { defaultValue: 'HTTPメソッド' })}
              </Form.Label>
              <Form.Select
                id={`${fieldId}-filter-http-method`}
                name="accessLogFilterHttpMethod"
                value={httpMethod}
                onChange={(e) => {
                  setHttpMethod(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-status-code`}>
                {t('access_logs.filters.status_code', { defaultValue: 'ステータスコード' })}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-filter-status-code`}
                name="accessLogFilterStatusCode"
                type="number"
                min={100}
                max={599}
                placeholder="-"
                value={statusCode}
                onChange={(e) => {
                  setStatusCode(e.target.value);
                  setPage(1);
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-path`}>
                {t('access_logs.filters.path', { defaultValue: 'ページ' })}
              </Form.Label>
              <Form.Select
                id={`${fieldId}-filter-path`}
                name="accessLogFilterPath"
                value={path}
                onChange={(e) => {
                  setPath(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                <option value="/">{t('access_logs.path.top', { defaultValue: 'トップ' })}</option>
                <option value="/register">{t('access_logs.path.register', { defaultValue: '新規登録' })}</option>
                <option value="/terms">{t('access_logs.path.terms', { defaultValue: '利用規約' })}</option>
                <option value="/privacy">{t('access_logs.path.privacy', { defaultValue: 'プライバシーポリシー' })}</option>
                {KENSUKE_FEATURE_CARD_DEFS.map((def) => (
                  <option key={def.id} value={def.id}>
                    {t('access_logs.path.feature_entrance_named', {
                      defaultValue: '入口: {{name}}',
                      name: t(def.titleKey),
                    })}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-app`}>
                {t('access_logs.filters.app', { defaultValue: 'アプリ' })}
              </Form.Label>
              <Form.Select
                id={`${fieldId}-filter-app`}
                value={appSurfaceFilter}
                onChange={(e) => {
                  setAppSurfaceFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                {Object.keys(ACCESS_LOG_APP_SURFACE_LABEL_KEYS).map((id) => (
                  <option key={id} value={id}>
                    {labelForAppSurface(id, t)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-public-access`}>
                {t('access_logs.filters.public_access', { defaultValue: 'アクセスタイプ' })}
              </Form.Label>
              <Form.Select
                id={`${fieldId}-filter-public-access`}
                name="accessLogFilterPublicAccess"
                value={isPublicAccess}
                onChange={(e) => {
                  setIsPublicAccess(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                <option value="true">{t('access_logs.table.public', { defaultValue: '公開' })}</option>
                <option value="false">{t('access_logs.table.private', { defaultValue: '非公開' })}</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-sample-id`}>
                {t('access_logs.filters.sample_id', { defaultValue: 'サンプルID' })}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-filter-sample-id`}
                name="accessLogFilterSampleId"
                type="number"
                min={1}
                placeholder="-"
                value={sampleId}
                onChange={(e) => {
                  setSampleId(e.target.value);
                  setPage(1);
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Label htmlFor={`${fieldId}-filter-user-id`}>
                {t('access_logs.filters.user', { defaultValue: 'ユーザーID' })}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-filter-user-id`}
                name="accessLogFilterUserId"
                type="number"
                min={1}
                placeholder="-"
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setPage(1);
                }}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <Button
                variant="outline-success"
                size="sm"
                disabled={exporting}
                onClick={handleExport}
                aria-busy={exporting}
              >
                {exporting
                  ? t('access_logs.exporting', { defaultValue: 'エクスポート中…' })
                  : t('access_logs.export_csv', { defaultValue: 'CSVエクスポート' })}
              </Button>
              {exportError && <span className="text-danger small ms-2">{exportError}</span>}
              <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleClearFilters}>
                {t('common.clear', { defaultValue: 'クリア' })}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 一覧・ページング */}
      {isLoading ? (
        <div className="text-center py-5" role="status" aria-label={t('common.loading', { defaultValue: '読み込み中' })}>
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {pagination && (
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2 border-bottom mb-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">
                  {pagination.total === 0
                    ? `0 ${t('access_logs.items', { defaultValue: '件' })}`
                    : `${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.total)} / ${pagination.total} ${t('access_logs.items', { defaultValue: '件' })}`}
                </span>
                <Form.Label className="visually-hidden" htmlFor={`${fieldId}-per-page`}>
                  {t('access_logs.per_page', { defaultValue: '件/ページ' })}
                </Form.Label>
                <Form.Select
                  id={`${fieldId}-per-page`}
                  name="accessLogPerPage"
                  size="sm"
                  style={{ width: 'auto' }}
                  value={LIMIT_OPTIONS.includes(limit) ? limit : LIMIT_OPTIONS[0]}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setLimit((LIMIT_OPTIONS.includes(n as typeof LIMIT_OPTIONS[number]) ? n : LIMIT_OPTIONS[0]) as typeof LIMIT_OPTIONS[number]);
                    setPage(1);
                  }}
                  aria-label={t('access_logs.per_page', { defaultValue: '件/ページ' })}
                >
                  {LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                      {t('access_logs.per_page', { defaultValue: '件/ページ' })}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="d-flex align-items-center gap-1">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage(1)}
                  title={t('access_logs.page_first', { defaultValue: '最初のページ' })}
                >
                  {t('access_logs.page_first_short', { defaultValue: '最初' })}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t('common.prev', { defaultValue: '前へ' })}
                </Button>
                <span className="px-2 small">
                  {t('access_logs.page_of', {
                    defaultValue: '{{current}} / {{total}}',
                    current: pagination.page,
                    total: Math.max(1, pagination.totalPages),
                  })}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('common.next', { defaultValue: '次へ' })}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage(Math.max(1, pagination.totalPages))}
                  title={t('access_logs.page_last', { defaultValue: '最後のページ' })}
                >
                  {t('access_logs.page_last_short', { defaultValue: '最後' })}
                </Button>
              </div>
            </div>
          )}
          <div className={`table-responsive ${styles.tableScroll}`}>
            <Table bordered hover size="sm" className={`mb-0 ${styles.tableCard}`}>
              <thead>
                <tr>
                  <th scope="col">{t('access_logs.table.header_access_time', { defaultValue: 'アクセス日時' })}</th>
                  <th scope="col">{t('access_logs.table.header_user', { defaultValue: 'ユーザー' })}</th>
                  <th scope="col">{t('access_logs.table.header_action', { defaultValue: 'アクション' })}</th>
                  <th scope="col">{t('access_logs.statistics.app_column', { defaultValue: 'アプリ' })}</th>
                  <th scope="col">{t('access_logs.table.header_http_method', { defaultValue: 'HTTP' })}</th>
                  <th scope="col">{t('access_logs.table.header_sample', { defaultValue: 'サンプル' })}</th>
                  <th scope="col">{t('access_logs.table.header_page', { defaultValue: 'パス' })}</th>
                  <th scope="col">{t('access_logs.table.header_ip_address', { defaultValue: 'IPアドレス' })}</th>
                  <th scope="col">{t('access_logs.table.header_country', { defaultValue: '国' })}</th>
                  <th scope="col">{t('access_logs.table.header_duration_ms', { defaultValue: '処理時間' })}</th>
                  <th scope="col">{t('access_logs.table.header_request_id', { defaultValue: 'リクエストID' })}</th>
                  <th scope="col">{t('access_logs.table.header_referer', { defaultValue: 'リファラー' })}</th>
                  <th scope="col">{t('access_logs.table.header_environment', { defaultValue: '環境' })}</th>
                  <th scope="col">{t('access_logs.table.header_access_type', { defaultValue: 'タイプ' })}</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="text-center text-muted py-3">
                      {t('access_logs.no_data', { defaultValue: '該当するログはありません。' })}
                    </td>
                  </tr>
                ) : null}
                {logs.map((log) => (
                  <tr key={String(log.id)}>
                    <td className="text-nowrap">
                      {log.accessTime && !Number.isNaN(new Date(log.accessTime).getTime())
                        ? format(new Date(log.accessTime), 'yyyy/MM/dd HH:mm:ss', { locale: dateFnsLocale })
                        : '-'}
                    </td>
                    <td className="text-nowrap">
                      {displayUser(log, t, i18n.language)}
                    </td>
                    <td className="text-nowrap">
                      <span>{formatActionDisplay(log, t)}</span>
                    </td>
                    <td className="text-nowrap small">
                      {labelForAppSurface(log.appSurface, t)}
                    </td>
                    <td className="text-nowrap">
                      {httpMethodBadge(log.httpMethod)}
                      {statusCodeBadge(log.statusCode)}
                    </td>
                    <td>{displaySample(log)}</td>
                    <td className={`small ${styles.pathCell}`} title={log.path ?? undefined}>
                      {formatPathDisplay(log, t)}
                    </td>
                    <td>
                      <span className="text-nowrap">{log.ipAddress}</span>
                      {log.ipChain && log.ipChain !== log.ipAddress && (
                        <div
                          className="small text-muted text-truncate"
                          style={{ maxWidth: 180 }}
                          title={log.ipChain}
                        >
                          {log.ipChain}
                        </div>
                      )}
                    </td>
                    <td className="text-nowrap small">{log.countryCode ?? '—'}</td>
                    <td className="text-end text-nowrap small">
                      {log.action === 'page_engagement' && log.durationMs != null
                        ? formatDurationMs(log.durationMs)
                        : log.durationMs != null
                          ? `${log.durationMs}ms`
                          : '—'}
                    </td>
                    <td
                      className="small font-monospace text-truncate"
                      style={{ maxWidth: 100 }}
                      title={log.requestId ?? ''}
                    >
                      {log.requestId ? `${log.requestId.slice(0, 8)}…` : '—'}
                    </td>
                    <td
                      className="small text-truncate"
                      style={{ maxWidth: 160 }}
                      title={log.referer ?? ''}
                    >
                      {log.referer
                        ? log.referer.length > 40
                          ? `${log.referer.slice(0, 40)}…`
                          : log.referer
                        : '—'}
                    </td>
                    <td
                      className="small text-nowrap"
                      title={log.userAgent ?? ''}
                    >
                      {log.clientEnvironment ?? summarizeUserAgent(log.userAgent)}
                    </td>
                    <td className="text-nowrap">
                      {log.isPublicAccess
                        ? t('access_logs.table.public', { defaultValue: '公開' })
                        : t('access_logs.table.private', { defaultValue: '非公開' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </Container>
  );
}
